import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { preloadListingFixtures } from '~portal/data/fixtures';
import { isPageTurn, isStandalone, logger } from '~portal/lib';
import { parseRegionsSearch, type RegionsSearchPatch, withRegionsSearch } from './-regions-listing';
import { RegionsListing } from './-RegionsListing';

/**
 * @desc The regions listing. Tab, filters, sort, view and page all live in the URL
 * (`parseRegionsSearch`), as on the admin usage page, so a filtered view can be shared or
 * bookmarked. Platform-only: the standalone build is the chat app alone, so it redirects to a new
 * chat like the other platform pages.
 */
export const Route = createFileRoute('/_protected/regions/')({
  beforeLoad: () => {
    if (isStandalone) {
      throw redirect({ to: '/chat/$conversationId', params: { conversationId: 'new' } });
    }
  },
  // The operators and regions fixtures are lazy chunks: fetch them alongside this route's own.
  loader: preloadListingFixtures,
  validateSearch: parseRegionsSearch,
  component: RegionsPage,
});

function RegionsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const update = (patch: RegionsSearchPatch) => {
    navigate({
      search: (prev) => withRegionsSearch(prev, patch),
      // A page turn starts at the top; a filter, sort or search keeps the reader in place.
      resetScroll: isPageTurn(patch),
    }).catch((err) => {
      logger.error('[Regions] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return <RegionsListing search={search} onSearch={update} />;
}
